import {
  type InboxEntry,
  type ResolutionEvidence,
  ResolutionEvidenceKind,
} from '@this+that/models';

type ResolutionFlagEntry = Pick<
  InboxEntry,
  'resolvedAt' | 'resolutionSuggestedAt' | 'autoResolvedSuggestionAt'
>;

export interface ResolutionFlag {
  kind: ResolutionEvidenceKind;
  stamp: number;
}

interface ResolveCurrentFlagOptions {
  // GraphQL entries do not select autoResolvedSuggestionAt; only WebSocket frames carry it.
  requireAutoStamp?: boolean;
}

export const resolveCurrentFlag = (
  entry: ResolutionFlagEntry,
  { requireAutoStamp = false }: ResolveCurrentFlagOptions = {},
): ResolutionFlag | null => {
  if (entry.resolvedAt) {
    const isAutoClose =
      !requireAutoStamp || entry.autoResolvedSuggestionAt === entry.resolvedAt;
    return isAutoClose
      ? { kind: ResolutionEvidenceKind.AUTO_RESOLVED, stamp: entry.resolvedAt }
      : null;
  }
  return entry.resolutionSuggestedAt
    ? {
        kind: ResolutionEvidenceKind.LOOKS_RESOLVED,
        stamp: entry.resolutionSuggestedAt,
      }
    : null;
};

// The store carries the field over on updates that lack it, so the stamp ties it to the flag it explains.
export const getCurrentResolutionEvidence = (
  entry: Pick<InboxEntry, 'resolutionEvidence'> & ResolutionFlagEntry,
): ResolutionEvidence | null => {
  const evidence = entry.resolutionEvidence;
  if (!evidence || evidence.communications.length === 0) return null;

  const flag = resolveCurrentFlag(entry);
  const isCurrent =
    flag?.kind === evidence.kind && flag.stamp === evidence.flaggedAt;

  return isCurrent ? evidence : null;
};
